// Subscription Plan Types (aligned with the backend ECaregiverPlan and ETraineePlan enums)
export type SubscriptionPlanType =
  | 'caregiver_monthly'
  | 'caregiver_3_month'
  | 'caregiver_annual'
  | 'caregiver_family'
  /** Activated by a partner-funded code, never purchased — not offered in the plan selector. */
  | 'caregiver_family_funded'
  | 'trainee_monthly'

/** The only plan an individual trainee can buy — kept here so callers never retype the string. */
export const TRAINEE_PLAN: SubscriptionPlanType = 'trainee_monthly'

// Source of the effective subscription (aligned with backend EntitlementSource)
export type EntitlementSource = 'group' | 'individual' | 'none'

/** Who pays: Stripe, or a partner organization through a partner-funded code. */
export type SubscriptionBillingType = 'paid' | 'partner_funded'

export type SubscriptionGroupRole = 'owner' | 'member'

export type SubscriptionGroupMemberStatus = 'invited' | 'active' | 'removed' | 'left'

export interface IFamilyContext {
  subscriptionGroupId: number
  role: SubscriptionGroupRole
  maxMembers: number
  membersActiveCount: number
  membersInvitedCount: number
  canInvite: boolean
  ownerUserId: number
}

export interface IFamilyMemberDto {
  id: number
  userId: number | null
  email: string
  firstname: string | null
  lastname: string | null
  role: SubscriptionGroupRole
  status: SubscriptionGroupMemberStatus
  invitedAt: string | null
  joinedAt: string | null
  removedAt: string | null
}

/** Effective entitlement returned by /subscription-groups/my and embedded in user DTO. */
export interface ICaregiverEntitlement {
  status: 'active' | 'inactive'
  source: EntitlementSource
  effectiveSubscription: UserSubscription | null
  familyContext: IFamilyContext | null
}

export interface IGetMySubscriptionGroupResponse {
  entitlement: ICaregiverEntitlement
  members: IFamilyMemberDto[]
}

export interface SubscriptionPlan {
  type: SubscriptionPlanType
  label: string
  price: number
  period: string
  /** Only set for shared/family plans — maximum number of caregivers per group. */
  maxSeats?: number
}

// Subscription Status
export enum SubscriptionStatus {
  Active = 'active',
  Trialing = 'trialing',
  PastDue = 'past_due',
  Canceled = 'canceled',
  Incomplete = 'incomplete',
  Unpaid = 'unpaid',
}

// Backend UserSubscription model (subset returned on auth user as lastSubscription)
export interface UserSubscription {
  id: number
  plan: SubscriptionPlanType
  status: SubscriptionStatus
  /** Absent on partner-funded subscriptions — they never touch Stripe. */
  stripeSubscriptionId?: string
  currentPeriodStart: string
  currentPeriodEnd: string
  /** Older sessions may still carry a DTO without it; treat missing as paid. */
  billingType?: SubscriptionBillingType
  cancelAtPeriodEnd: boolean
  canceledAt: string | null
  endedAt: string | null
  /** Set when an active subscription has a plan change scheduled for period end */
  pendingPlan?: SubscriptionPlanType | null
  pendingStripePriceId?: string | null
  pendingPlanEffectiveAt?: string | null
}

/** `POST /user-subscriptions/cancel` — auto-renewal off, access kept until the period ends. */
export interface CancelSubscriptionResponse {
  source: 'individual' | 'group'
  cancelAtPeriodEnd: boolean
  currentPeriodEnd?: string
  stripeSubscriptionId: string
}

// Backend UserBillingProfile model (card info)
export interface UserBillingProfile {
  id: number
  stripeCustomerId: string
  defaultPaymentMethodId: string | null
  cardBrand: string | null
  cardLast4: string | null
  cardExpMonth: number | null
  cardExpYear: number | null
}

// Pay Now response
export interface PayNowResponse {
  billingProfileId: number
  subscriptionId: number
  stripeInvoiceId: string
  stripePaymentIntentId?: string
  paymentIntentClientSecret?: string
  status: 'paid' | 'requires_action'
  subscriptionStatus?: string
  alreadyPaid?: boolean
}

// Payment Status
export enum PaymentStatus {
  Paid = 'paid',
  Failed = 'failed',
  Refunded = 'refunded',
  Pending = 'pending',
}

// Payment Method (card info from Stripe)
export interface PaymentMethod {
  id: string
  brand: string // visa, mastercard, amex, etc.
  last4: string
  expMonth: number
  expYear: number
}

// Subscription Info
export interface SubscriptionInfo {
  id: string
  planType: SubscriptionPlanType
  status: SubscriptionStatus
  renewalDate: string // ISO date string
  paymentMethod: PaymentMethod | null
}

// Payment History Item
export interface PaymentHistoryItem {
  id: string
  date: string // ISO date string
  planType: SubscriptionPlanType
  status: PaymentStatus
  amount: number
}
